/* eslint-disable func-names */
import React, { useRef, useState } from 'react';
import { notify } from '@lib/notifier/notify';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Flex, Box } from 'rebass';
import { Typography, Avatar, Button, ConfirmationDialog } from '@oca/ui';
import { AccessControl } from '@features/common';
import { Person, Pencil, PersonDelete, Key, Plus } from '@oca/icons';
import { Trans } from '@lingui/macro';
import { history } from '@lib/routing';
import profileBackground from '../assets/profile-bg.png';
import { updateMyAvatar, uploadEmployeeAvatar } from '../api';

const PositionText = (department, position) => {
  switch (position) {
    case 'director':
      return <Trans>Director</Trans>;
    case 'manager':
      return <Trans>{department} department manager</Trans>;
    default:
      return <Trans>{department} expert</Trans>;
  }
};

export function ProfileHeader({
  children,
  profile,
  profilePath,
  onChangePassword,
  onTerminate,
  viewerId,
}) {
  const isActive = profile.status === 'active';
  const isTerminated = profile.status === 'terminate';
  const canTerminate = viewerId !== profile.id && isActive;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const [avatarSrc, setAvatarSrc] = React.useState(profile.avatar);

  React.useEffect(
    function() {
      setAvatarSrc(profile.avatar);
    },
    [profile.avatar],
  );

  const handleAvatarClick = () => {
    // eslint-disable-next-line no-unused-expressions
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = e => {
    const file = e && e.target && e.target.files ? e.target.files[0] : null;

    if (!file) return;

    setUploading(true);

    uploadEmployeeAvatar(file)
      .then(function(res) {
        const data = res && res.data ? res.data : null;
        if (!data) throw new Error('Upload failed');

        const payload = {
          avatar: data.file,
          avatar_url: data.fileUrl,
          avatar_name: data.fileName,
        };

        return updateMyAvatar(payload).then(function() {
          if (data && data.fileUrl) {
            // eslint-disable-next-line prefer-template
            const bust = 'v=' + (data.id || new Date().getTime());
            setAvatarSrc(
              data.fileUrl +
                (data.fileUrl.indexOf('?') >= 0 ? '&' : '?') +
                bust,
            );
          }
        });
      })
      .then(function() {
        notify.success('Avatar updated successfully');
        if (fileInputRef.current) fileInputRef.current.value = '';
      })
      .catch(function() {
        notify.error('Failed to upload avatar');
        if (fileInputRef.current) fileInputRef.current.value = '';
      })
      .then(function() {
        setUploading(false);
      });
  };

  return (
    <HeaderContainer>
      <Cover backgroundImage={!isTerminated ? profileBackground : null} />

      {/* Content Section */}
      <ContentWrapper>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Flex alignItems="center" flex="1" minWidth="250px">
            <AvatarWrapper>
              <Avatar
                key={avatarSrc}
                src={avatarSrc}
                borderRadius="50%"
                size={120}
              >
                <Person />
              </Avatar>

              {viewerId === profile.id && (
                <>
                  <UploadButton
                    onClick={handleAvatarClick}
                    disabled={uploading}
                  >
                    <Plus size={16} color="#fff" />
                  </UploadButton>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </>
              )}
            </AvatarWrapper>

            <Box>
              <Typography fontSize={30} fontWeight={500}>
                {profile.fullName}
              </Typography>
              <Typography variant="body" fontSize={16} color="textSecondary">
                {profile.jobTitle ||
                  PositionText(profile.department.name, profile.position)}
              </Typography>
              {!isActive && (
                <StatusBadge>
                  <Trans>Terminated</Trans>
                </StatusBadge>
              )}
            </Box>
          </Flex>

          {/* Action Buttons */}
          <Flex alignItems="center" style={{ gap: '10px' }}>
            <AccessControl allowed={['admin']}>
              {isActive && (
                <Button
                  variant="secondary"
                  onClick={() => history.push(`${profilePath}/edit`)}
                >
                  <Pencil />
                  <Trans>Edit</Trans>
                </Button>
              )}
              {canTerminate && (
                <ConfirmationDialog
                  title={<Trans>Terminate {profile.fullName}?</Trans>}
                  description={
                    <Trans>
                      This employee will be marked as terminated and will
                      disappear from employee list.
                    </Trans>
                  }
                  confirmText={<Trans>Terminate</Trans>}
                >
                  {confirm => (
                    <Button
                      variant="danger"
                      onClick={confirm(onTerminate)}
                      disabled={profile.status === 'terminate'}
                    >
                      <PersonDelete /> <Trans>Terminate</Trans>
                    </Button>
                  )}
                </ConfirmationDialog>
              )}
            </AccessControl>

            {viewerId === profile.id && (
              <Button variant="primary" onClick={onChangePassword}>
                <Key />
                <Trans>Change password</Trans>
              </Button>
            )}
            {/* 
            <Flex alignItems="center" ml={3}>
              <LocaleSelect />
            </Flex> */}
          </Flex>
        </Flex>

        <Box width={1} pb={3} borderTop="1px solid #eee">
          {children}
        </Box>
      </ContentWrapper>
    </HeaderContainer>
  );
}

ProfileHeader.propTypes = {
  children: PropTypes.node.isRequired,
  profile: PropTypes.shape({
    id: PropTypes.number,
    fullName: PropTypes.string,
    avatar: PropTypes.string,
    department: PropTypes.shape({
      name: PropTypes.string,
    }),
    position: PropTypes.string,
    jobTitle: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  profilePath: PropTypes.string.isRequired,
  onTerminate: PropTypes.func.isRequired,
  onChangePassword: PropTypes.func.isRequired,
  viewerId: PropTypes.number.isRequired,
};

/* ---------------- Styled Components ---------------- */

const HeaderContainer = styled(Box)`
  overflow: hidden;
`;

const Cover = styled.div`
  height: 200px;
  background: ${({ backgroundImage }) =>
    backgroundImage
      ? `url(${backgroundImage}) center/cover no-repeat`
      : '#333'};
  border-radius: 12px 12px 0 0;
  position: relative;
`;

const ContentWrapper = styled(Box)`
  padding: 24px;
  padding-bottom: 0px;
  margin-top: -40px;
  background: #fff;
  border-radius: 12px 12px 0 0;
`;

const AvatarWrapper = styled(Box)`
  margin-right: 20px;
  position: relative;
  top: -20px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: white;
`;

const StatusBadge = styled.span`
  display: inline-block;
  background: #ff4d4f;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
  margin-top: 4px;
`;

const UploadButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #7738ff;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #7738ffe7;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
